import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, FilePlus2, Search } from "lucide-react";

import { leaveApi } from "../api";
import { LeaveBackButton } from "../components/LeaveBackButton";
import { LeaveError } from "../components/LeaveError";
import { LeaveLoading } from "../components/LeaveLoading";
import { LeavePageHeader } from "../components/LeavePageHeader";
import { LeavePagination } from "../components/LeavePagination";
import { LeaveStatusBadge } from "../components/LeaveStatusBadge";
import {
  EmptyState,
  LinkBtn,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "../components/LeaveUI";
import { useAsyncData, usePagination } from "../hooks";
import { clearDraft, formatDraftAge, loadDraft } from "../lib/draft";
import type { LeaveApplicationSummary, LeaveStatus } from "../types";

const STATUS_TABS: { label: string; value: LeaveStatus | "ALL" }[] = [
  { label: "All",       value: "ALL" },
  { label: "Pending",   value: "PENDING_APPROVAL" },
  { label: "Approved",  value: "APPROVED" },
  { label: "Drafts",    value: "DRAFT" },
  { label: "Rejected",  value: "REJECTED" },
  { label: "Cancelled", value: "CANCELLED" },
];

export default function LeaveRequestsPage() {
  const pagination = usePagination();
  const [statusFilter, setStatusFilter] = useState<LeaveStatus | "ALL">("ALL");
  const [search, setSearch] = useState("");

  const { data, loading, error, reload } = useAsyncData(
    () => leaveApi.myApplications(pagination.page, pagination.size),
    [pagination.page, pagination.size],
  );

  const employee = useAsyncData(() => leaveApi.currentEmployee(), []);
  const [draft, setDraft] = useState<ReturnType<typeof loadDraft>>(null);

  useEffect(() => {
    if (employee.data?.id) setDraft(loadDraft(employee.data.id));
  }, [employee.data?.id]);

  function discardDraft() {
    if (employee.data?.id) clearDraft(employee.data.id);
    setDraft(null);
  }

  const filteredItems: LeaveApplicationSummary[] = useMemo(() => {
    if (!data?.content) return [];
    return data.content.filter((item) => {
      const matchStatus = statusFilter === "ALL" || item.status === statusFilter;
      const q = search.trim().toLowerCase();
      const matchSearch =
        !q ||
        item.applicationNo.toLowerCase().includes(q) ||
        item.leaveTypeName.toLowerCase().includes(q) ||
        (item.currentStepName ?? "").toLowerCase().includes(q);
      return matchStatus && matchSearch;
    });
  }, [data?.content, statusFilter, search]);

  const countByStatus = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const item of data?.content ?? []) {
      counts[item.status] = (counts[item.status] ?? 0) + 1;
    }
    return counts;
  }, [data?.content]);

  return (
    <div className="space-y-6">
      <LeavePageHeader
        breadcrumb="Leave Management"
        title="My Leave Requests"
        actions={
          <div className="flex items-center gap-2">
            <LeaveBackButton to="/hr/leave" label="Dashboard" />
            <LinkBtn to="/hr/leave/requests/new" variant="primary" icon={FilePlus2}>
              Apply for Leave
            </LinkBtn>
          </div>
        }
      />

      {/* Draft resume banner */}
      {draft && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-200">
              <CalendarDays className="h-4 w-4 text-amber-800" />
            </div>
            <div>
              <p className="font-semibold text-amber-900">Unsaved draft</p>
              <p className="text-sm text-amber-700">
                Saved {formatDraftAge(draft.savedAt)} — continue where you left off.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Link
              to="/hr/leave/requests/new"
              className="rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-amber-700"
            >
              Resume Draft
            </Link>
            <button
              type="button"
              onClick={discardDraft}
              className="rounded-xl border border-amber-300 px-4 py-2 text-sm font-semibold text-amber-700 transition hover:bg-amber-100"
            >
              Discard
            </button>
          </div>
        </div>
      )}

      {/* Filter toolbar */}
      <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
        {/* Status filter pills */}
        <div className="flex flex-wrap gap-1.5 border-b px-4 py-3">
          {STATUS_TABS.map((tab) => {
            const count  = tab.value === "ALL" ? (data?.totalElements ?? 0) : (countByStatus[tab.value] ?? 0);
            const active = statusFilter === tab.value;
            return (
              <button
                key={tab.value}
                type="button"
                onClick={() => { setStatusFilter(tab.value); pagination.reset(); }}
                className={`inline-flex items-center gap-1.5 rounded-xl px-3.5 py-1.5 text-sm font-semibold transition-colors ${
                  active
                    ? "bg-emerald-600 text-white shadow-sm"
                    : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                }`}
              >
                {tab.label}
                {count > 0 && (
                  <span className={`rounded-full px-1.5 py-0.5 text-xs font-bold ${
                    active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="px-4 py-3">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              className="w-full rounded-xl border border-slate-200 py-2.5 pl-10 pr-4 text-sm outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              placeholder="Search by application no., leave type or workflow step…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {loading && <LeaveLoading label="Loading leave requests…" />}
      {error   && <LeaveError message={error} onRetry={reload} />}

      {!loading && data && (
        <>
          <RequestsTable items={filteredItems} />
          <LeavePagination
            page={data}
            onPageChange={pagination.setPage}
            onSizeChange={pagination.setSize}
          />
        </>
      )}
    </div>
  );
}

function RequestsTable({ items }: { items: LeaveApplicationSummary[] }) {
  function fmtDate(v?: string | null) {
    if (!v) return "—";
    try {
      return new Date(`${v}T00:00:00`).toLocaleDateString(undefined, {
        day: "numeric", month: "short", year: "numeric",
      });
    } catch { return v; }
  }

  return (
    <Table>
      <THead>
        <TH>Application</TH>
        <TH>Leave Type</TH>
        <TH>Start</TH>
        <TH>End</TH>
        <TH>Days</TH>
        <TH>Step</TH>
        <TH>Status</TH>
        <TH right>Action</TH>
      </THead>
      <TBody>
        {items.length === 0 ? (
          <tr>
            <td colSpan={8}>
              <EmptyState
                icon={CalendarDays}
                title="No leave requests found"
                description="Your applications will appear here once submitted."
                action={
                  <LinkBtn to="/hr/leave/requests/new" variant="primary" icon={FilePlus2}>
                    Apply for Leave
                  </LinkBtn>
                }
              />
            </td>
          </tr>
        ) : (
          items.map((item) => (
            <TR key={item.id}>
              <TD>
                <span className="font-mono text-xs font-semibold text-slate-600">
                  {item.applicationNo}
                </span>
              </TD>
              <TD className="font-medium text-slate-800">{item.leaveTypeName}</TD>
              <TD muted>{fmtDate(item.startDate)}</TD>
              <TD muted>{fmtDate(item.endDate)}</TD>
              <TD>
                <span className="rounded-lg bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-700">
                  {item.requestedDays}d
                </span>
              </TD>
              <TD muted className="text-xs">{item.currentStepName || "—"}</TD>
              <TD><LeaveStatusBadge value={item.status} /></TD>
              <TD right>
                <Link
                  to={`/hr/leave/requests/${item.id}`}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700"
                >
                  View →
                </Link>
              </TD>
            </TR>
          ))
        )}
      </TBody>
    </Table>
  );
}
